import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useApi, type AiProposal, type ApiClient, type PagedResult } from "@ncp/api-client";
import type { Thread } from "./types";
import { useLetterPreview, type StepLettersAnswer } from "@ncp/ui/components/step-letters";

// ---------------------------------------------------------------- types (mirror Ncp.Api/Contracts/MatterContracts.cs)

export interface PlaybookParameterOption { value: string; label: string; labelAr: string; collectionKey?: string | null; legalBasis?: string | null }

export interface PlaybookParameter {
  key: string;
  label: string;
  labelAr: string;
  /** text | textarea | select | date | number */
  kind: string;
  required: boolean;
  options?: PlaybookParameterOption[] | null;
  help?: string | null;
  helpAr?: string | null;
}

export interface PlaybookCard {
  key: string;
  name: string;
  nameAr: string | null;
  summary: string | null;
  summaryAr: string | null;
  icon: string;
  category: string;
  categoryAr: string | null;
  code: string;
  workspaceKey: string | null;
  processingTime: string | null;
  processingTimeAr: string | null;
  legalBasis: string | null;
  intakeFormKey: string | null;
  parameters: PlaybookParameter[];
  agentKeys: string[];
  starterPrompt: string | null;
  starterPromptAr: string | null;
  /** Cron (UTC) when the playbook recurs. */
  schedule: string | null;
  isCompiled: boolean;
  /** The documents the playbook expects, filed under these slot keys. */
  documents: PlaybookDocumentSlot[];
  /** Outside systems (the OutSystems e-services) may submit to it through the machine API. */
  acceptsExternal: boolean;
  /** Workbench tabs the playbook adds after Overview (a workspace may override them per playbook). */
  modules: string[];
}

export interface PlaybookDocumentSlot { key: string; label: string; labelAr: string; required: boolean; hint: string | null; hintAr: string | null }

/** A published form in the shape `@ncp/forms` renders (`FormDocument`). */
export interface MatterFormDocument {
  key: string;
  version: number;
  name: { en?: string; ar?: string };
  schema: unknown;
  uiSchema: unknown;
  rules?: unknown;
}

export interface PlaybookDetail { playbook: PlaybookCard; intakeForm: MatterFormDocument | null }

export interface MatterRow {
  id: string;
  number: string;
  title: string;
  titleAr: string | null;
  serviceKey: string | null;
  serviceName: string | null;
  serviceNameAr: string | null;
  icon: string;
  workspaceKey: string | null;
  isCompiled: boolean;
  state: string;
  stateLabel: string | null;
  stateLabelAr: string | null;
  status: string;
  priority: string;
  assigneeUserId: string | null;
  assigneeName: string | null;
  openTasks: number;
  myOpenTasks: number;
  nextDeadline: string | null;
  nextDeadlineLabel: string | null;
  deadlineHealth: string | null;
  createdAt: string;
  updatedAt: string;
  /** Position of the current state in the playbook's graph: the board's lane order. */
  stageOrder: number;
  nextDeadlineStartedAt: string | null;
  /** The caller's earliest open task on the file, otherwise the file's. */
  nextStep?: string | null;
  nextStepAr?: string | null;
  nextStepIsMine?: boolean;
}

export interface MatterList { items: MatterRow[]; total: number }

export interface MatterFilter {
  workspace?: string | null;
  service?: string | null;
  /** open (default) | closed | all */
  status?: string;
  mine?: boolean;
  q?: string;
  skip?: number;
  take?: number;
}

export interface MatterParameterValue { key: string; label: string; labelAr: string | null; value: string | null; displayValue: string | null; displayValueAr: string | null }
export interface MatterStage { key: string; label: string; labelAr: string | null; kind: string; status: string; isCurrent: boolean; isDone: boolean }
export interface MatterTransition {
  trigger: string;
  to: string;
  label: string;
  labelAr: string | null;
  allowedRoles: string[];
  automatic: boolean;
  guard: string | null;
  guardSatisfied: boolean;
  permittedForCurrentUser: boolean;
}

export interface MatterTask {
  id: string;
  key: string;
  title: string;
  titleAr: string | null;
  description: string | null;
  requiredRole: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  status: string;
  dueAt: string | null;
  completedAt: string | null;
  completedByName: string | null;
  workflowState: string | null;
  completionTrigger: string | null;
  formKey: string | null;
  formData: Record<string, unknown> | null;
  completionNote: string | null;
  isMine: boolean;
  isOpen: boolean;
  createdAt: string;
}

export interface MatterClock {
  id: string;
  key: string;
  label: string;
  labelAr: string;
  legalBasis: string | null;
  startedAt: string;
  dueAt: string;
  completedAt: string | null;
  pausedAt: string | null;
  health: string;
  isPrimary: boolean;
  durationDays: number;
  workingDays: boolean;
}

export interface MatterDocument {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  category: string;
  version: number;
  uploadedByName: string;
  uploadedByKind: string;
  createdAt: string;
  extractionStatus: string;
  pageCount: number | null;
  summary: string | null;
  summaryAr: string | null;
  /** Shared with the applicant's channel (the e-service shows and downloads it). */
  visibleToApplicant: boolean;
  avScanPassed: boolean;
}

/** The person or company the matter concerns – from the intake answers or the submitting e-service. */
export interface MatterApplicant { id: string; name: string; nameAr: string | null; identifier: string | null; emirate: string | null; email: string | null; phone: string | null; isSubmitter: boolean }

export interface MatterEvent { id: string; kind: string; title: string; titleAr: string | null; detail: string | null; actorName: string | null; actorKind: string; fromState: string | null; toState: string | null; occurredAt: string }

export interface MatterProposal { id: string; agentKey: string; agentName: string; kind: string; title: string; titleAr: string | null; summary: string; status: string; confidence: number; requiredRole: string | null; threadId: string | null; createdAt: string }

export interface MatterArtifact { id: string; threadId: string; key: string; version: number; kind: string; title: string; titleAr: string | null; agentKey: string | null; caseDocumentId: string | null; updatedAt: string }

export interface MatterRun { id: string; agentKey: string; agentName: string; status: string; triggerSource: string; workflowState: string | null; startedAt: string; completedAt: string | null; proposalId: string | null; error: string | null }

export interface MatterDetail {
  id: string;
  number: string;
  title: string;
  titleAr: string | null;
  summary: string | null;
  state: string;
  status: string;
  priority: string;
  workspaceKey: string | null;
  serviceKey: string | null;
  playbook: PlaybookCard | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  isOpen: boolean;
  parameters: MatterParameterValue[];
  intake: Record<string, unknown> | null;
  intakeLines: string[];
  stages: MatterStage[];
  transitions: MatterTransition[];
  tasks: MatterTask[];
  clocks: MatterClock[];
  documents: MatterDocument[];
  events: MatterEvent[];
  proposals: MatterProposal[];
  artifacts: MatterArtifact[];
  runs: MatterRun[];
  threadId: string | null;
  collections: string[];
  source: string | null;
  originThreadId: string | null;
  externalReference: string | null;
  canWork: boolean;
  /** Holds `matters.decide`: may leave a decision state (record the outcome). */
  canDecide: boolean;
  /** The matter waits at a decision node. */
  atDecision: boolean;
  isCompiled: boolean;
  /** Client id of the system that submitted it (an e-service), and that client's name. */
  externalSource: string | null;
  externalSourceName: string | null;
  applicant: MatterApplicant | null;
  /** Required document slots nothing has been filed under yet. */
  missingDocuments: PlaybookDocumentSlot[];
  /** The space use case the matter was opened from, and the top-level keys of its template. */
  useCase?: string | null;
  templateKeys?: string[] | null;
}

export interface MatterPerson { id: string; displayName: string; displayNameAr: string | null; title: string | null; roles: string[] }

export interface OpenMatterRequest {
  serviceKey: string;
  title?: string | null;
  titleAr?: string | null;
  summary?: string | null;
  parameters?: Record<string, string | null>;
  intake?: Record<string, unknown> | null;
  assigneeUserId?: string | null;
  priority?: string | null;
  threadId?: string | null;
  locale?: string;
  /** Open it in this space (one that offers the playbook) rather than the playbook's home. */
  workspaceKey?: string | null;
  /** The space use case it starts from: its template is copied onto the matter for the matter's pages. */
  useCase?: string | null;
}

/** A file attached when the matter is opened, filed under a playbook document slot (or "Other"). */
export interface MatterAttachment { file: File; category?: string | null }

// ---------------------------------------------------------------- client

export const MATTERS_API = "/api/matters";

export class MattersApi {
  constructor(private readonly api: ApiClient) {}

  playbooks(workspace?: string | null): Promise<PlaybookCard[]> {
    return this.api.get<PlaybookCard[]>(`${MATTERS_API}/playbooks`, { workspace: workspace ?? undefined });
  }

  playbook(key: string): Promise<PlaybookDetail> {
    return this.api.get<PlaybookDetail>(`${MATTERS_API}/playbooks/${encodeURIComponent(key)}`);
  }

  form(key: string): Promise<MatterFormDocument> {
    return this.api.get<MatterFormDocument>(`${MATTERS_API}/forms/${encodeURIComponent(key)}`);
  }

  people(workspace: string): Promise<MatterPerson[]> {
    return this.api.get<MatterPerson[]>(`${MATTERS_API}/people`, { workspace });
  }

  list(filter: MatterFilter = {}): Promise<MatterList> {
    return this.api.get<MatterList>(MATTERS_API, { ...filter, workspace: filter.workspace ?? undefined, service: filter.service ?? undefined });
  }

  get(id: string): Promise<MatterDetail> {
    return this.api.get<MatterDetail>(`${MATTERS_API}/${id}`);
  }

  /** Opens a matter; attachments go in the same request so they are filed before the first stage's agents run. */
  open(input: OpenMatterRequest, attachments: MatterAttachment[] = []): Promise<MatterDetail> {
    if (attachments.length === 0) return this.api.post<MatterDetail>(MATTERS_API, input);
    const form = new FormData();
    form.append("payload", JSON.stringify(input));
    for (const a of attachments) {
      form.append("files", a.file, a.file.name);
      form.append("categories", a.category || "Other");
    }
    return this.api.upload<MatterDetail>(MATTERS_API, form);
  }

  /** Shares a document with the applicant's channel, or withdraws it. */
  shareDocument(id: string, documentId: string, shared: boolean): Promise<MatterDocument> {
    return this.api.post<MatterDocument>(`${MATTERS_API}/${id}/documents/${documentId}/share`, { shared });
  }

  /** A move, with the fingerprint of the letters it sends as the officer read them (release 1.6); none when it sends no letter. */
  fire(id: string, trigger: string, note?: string | null, lettersFingerprint?: string | null): Promise<MatterDetail> {
    return this.api.post<MatterDetail>(`${MATTERS_API}/${id}/transitions`, { trigger, note, lettersFingerprint: lettersFingerprint ?? null });
  }

  /** The letters a move would send, in full and filled from the matter; nothing is sent (release 1.6). */
  stepLetters(id: string, trigger: string, note?: string | null): Promise<StepLettersAnswer> {
    return this.api.post<StepLettersAnswer>(`${MATTERS_API}/${id}/transitions/${encodeURIComponent(trigger)}/letters`, { note: note ?? null });
  }

  /** The letters completing a task would send through the step it takes; nothing is sent (release 1.6). */
  taskLetters(id: string, taskId: string): Promise<StepLettersAnswer> {
    return this.api.get<StepLettersAnswer>(`${MATTERS_API}/${id}/tasks/${taskId}/letters`);
  }

  /** `lettersFingerprint`: the letters the task's step sends, as the officer read them (release 1.6); none when it sends no letter. */
  completeTask(id: string, taskId: string, body: { data?: Record<string, unknown> | null; note?: string | null; locale?: string; lettersFingerprint?: string | null }): Promise<{ matter: MatterDetail; transitionError: string | null }> {
    return this.api.post(`${MATTERS_API}/${id}/tasks/${taskId}/complete`, body);
  }

  assign(id: string, assigneeUserId: string | null): Promise<MatterDetail> {
    return this.api.post<MatterDetail>(`${MATTERS_API}/${id}/assign`, { assigneeUserId });
  }

  note(id: string, text: string): Promise<MatterEvent> {
    return this.api.post<MatterEvent>(`${MATTERS_API}/${id}/notes`, { text });
  }

  upload(id: string, file: File, category = "Other"): Promise<MatterDocument> {
    const form = new FormData();
    form.append("file", file);
    form.append("category", category);
    return this.api.upload<MatterDocument>(`${MATTERS_API}/${id}/documents`, form);
  }

  /** The caller's conversation about the matter (created with the workspace's default assistant when there is none). */
  thread(id: string): Promise<Thread> {
    return this.api.post<Thread>(`${MATTERS_API}/${id}/thread`);
  }

  /** Download URL of a matter document (served by the case documents API, which applies the same access policy). */
  static documentPath(matterId: string, documentId: string): string {
    return `/api/cases/${matterId}/documents/${documentId}/download`;
  }
}

// ---------------------------------------------------------------- hooks

export const matterKeys = {
  all: ["matters"] as const,
  playbooks: (workspace?: string | null) => ["matters", "playbooks", workspace ?? "*"] as const,
  playbook: (key: string) => ["matters", "playbook", key] as const,
  form: (key: string) => ["matters", "form", key] as const,
  people: (workspace: string) => ["matters", "people", workspace] as const,
  list: (filter: MatterFilter = {}) => ["matters", "list", filter] as const,
  detail: (id: string) => ["matters", "detail", id] as const,
  proposals: (id: string) => ["matters", "proposals", id] as const,
};

type Opts<T> = Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, "queryKey" | "queryFn">;

export function useMattersApi(): MattersApi {
  const api = useApi();
  return useMemo(() => new MattersApi(api), [api]);
}

export function usePlaybooks(workspace?: string | null, options?: Opts<PlaybookCard[]>) {
  const sdk = useMattersApi();
  return useQuery({ queryKey: matterKeys.playbooks(workspace), queryFn: () => sdk.playbooks(workspace), staleTime: 60_000, ...options });
}

export function usePlaybook(key: string | null | undefined, options?: Opts<PlaybookDetail>) {
  const sdk = useMattersApi();
  return useQuery({ queryKey: matterKeys.playbook(key ?? ""), queryFn: () => sdk.playbook(key!), enabled: !!key, staleTime: 60_000, ...options });
}

export function useMatterForm(key: string | null | undefined, options?: Opts<MatterFormDocument>) {
  const sdk = useMattersApi();
  return useQuery({ queryKey: matterKeys.form(key ?? ""), queryFn: () => sdk.form(key!), enabled: !!key, staleTime: 5 * 60_000, ...options });
}

export function useWorkspacePeople(workspace: string | null | undefined, options?: Opts<MatterPerson[]>) {
  const sdk = useMattersApi();
  return useQuery({ queryKey: matterKeys.people(workspace ?? ""), queryFn: () => sdk.people(workspace!), enabled: !!workspace, staleTime: 5 * 60_000, ...options });
}

export function useMatters(filter: MatterFilter = {}, options?: Opts<MatterList>) {
  const sdk = useMattersApi();
  return useQuery({ queryKey: matterKeys.list(filter), queryFn: () => sdk.list(filter), ...options });
}

export function useMatter(id: string | null | undefined, options?: Opts<MatterDetail>) {
  const sdk = useMattersApi();
  return useQuery({ queryKey: matterKeys.detail(id ?? ""), queryFn: () => sdk.get(id!), enabled: !!id, ...options });
}

/** Writes return the refreshed matter; it replaces the cached detail and every list is refetched. */
function useMatterWrite<TVars>(fn: (sdk: MattersApi, vars: TVars) => Promise<MatterDetail>) {
  const sdk = useMattersApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: TVars) => fn(sdk, vars),
    onSuccess: (m) => {
      qc.setQueryData(matterKeys.detail(m.id), m);
      void qc.invalidateQueries({ queryKey: ["matters", "list"] });
    },
  });
}

export function useOpenMatter() {
  return useMatterWrite((sdk, input: OpenMatterRequest & { attachments?: MatterAttachment[] }) => {
    const { attachments, ...request } = input;
    return sdk.open(request, attachments ?? []);
  });
}

export function useShareMatterDocument(id: string) {
  const sdk = useMattersApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { documentId: string; shared: boolean }) => sdk.shareDocument(id, vars.documentId, vars.shared),
    onSuccess: () => void qc.invalidateQueries({ queryKey: matterKeys.detail(id) }),
  });
}

export function useFireMatterTransition(id: string) {
  return useMatterWrite((sdk, vars: { trigger: string; note?: string | null; lettersFingerprint?: string | null }) => sdk.fire(id, vars.trigger, vars.note, vars.lettersFingerprint));
}

/** The letters completing a task of the matter would send, read for the task as it now stands (release 1.6). */
export function useMatterTaskLetters(id: string, taskId: string | null) {
  const sdk = useMattersApi();
  return useLetterPreview(taskId ? `matter:${id}:task:${taskId}` : null, taskId, (t) => sdk.taskLetters(id, t!));
}

/** The letters a move of the matter would send, read for the move and note as they now stand (release 1.6). */
export function useMatterMoveLetters(id: string, trigger: string | null, note: string | null) {
  const sdk = useMattersApi();
  return useLetterPreview(trigger ? `matter:${id}:${trigger}` : null, { trigger, note }, (r) => sdk.stepLetters(id, r.trigger!, r.note));
}

export function useAssignMatter(id: string) {
  return useMatterWrite((sdk, assigneeUserId: string | null) => sdk.assign(id, assigneeUserId));
}

export function useCompleteMatterTask(id: string) {
  const sdk = useMattersApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string; data?: Record<string, unknown> | null; note?: string | null; locale?: string; lettersFingerprint?: string | null }) =>
      sdk.completeTask(id, vars.taskId, { data: vars.data, note: vars.note, locale: vars.locale, lettersFingerprint: vars.lettersFingerprint ?? null }),
    onSuccess: (r) => {
      qc.setQueryData(matterKeys.detail(id), r.matter);
      void qc.invalidateQueries({ queryKey: ["matters", "list"] });
    },
  });
}

export function useMatterNote(id: string) {
  const sdk = useMattersApi();
  const qc = useQueryClient();
  return useMutation({ mutationFn: (text: string) => sdk.note(id, text), onSuccess: () => void qc.invalidateQueries({ queryKey: matterKeys.detail(id) }) });
}

export function useUploadMatterDocument(id: string) {
  const sdk = useMattersApi();
  const qc = useQueryClient();
  return useMutation({ mutationFn: (vars: { file: File; category?: string }) => sdk.upload(id, vars.file, vars.category), onSuccess: () => void qc.invalidateQueries({ queryKey: matterKeys.detail(id) }) });
}

export function useMatterThread(id: string) {
  const sdk = useMattersApi();
  return useMutation({ mutationFn: () => sdk.thread(id) });
}

/** Full proposals of a matter (the shape `ProposalList` reviews), from the same inbox API the back office uses. */
export function useMatterProposals(id: string | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: matterKeys.proposals(id ?? ""),
    queryFn: () => api.get<PagedResult<AiProposal>>("/api/ai/proposals", { caseId: id!, pageSize: 100 }),
    enabled: !!id,
  });
}
